/**
 * Piano or on the go.
 *
 * A segmented control rather than a switch, because the two halves are not on/off — they
 * are two places to be, and neither is a degraded version of the other. A switch labelled
 * "on the go" would read as "turn off the good mode", which is exactly backwards for
 * someone on a train who came here to think about harmony.
 *
 * The tap that lands here is a user gesture, and the host depends on that: coming back to
 * the piano resumes a suspended `AudioContext`, which Safari permits only on this stack.
 * So this must stay a real button and must not be debounced, deferred or animated first.
 */

import type { SessionMode, SessionView } from './sessionView.js';

export interface ModeToggleProps {
  mode: SessionMode;
  onChange: (mode: SessionMode) => void;
  /**
   * False where a microphone cannot exist at all — an insecure origin, a browser with no
   * `AudioWorklet`. The piano half is still shown, because hiding it would leave someone
   * wondering what this app is for, but it is disabled and says why.
   */
  pianoAvailable?: boolean;
}

const OPTIONS: ReadonlyArray<{ mode: SessionMode; label: string; hint: string }> = Object.freeze([
  { mode: 'piano', label: 'Piano', hint: 'Listens to the room' },
  { mode: 'on-the-go', label: 'On the go', hint: 'Tap chords instead' },
]);

export function ModeToggle({
  mode,
  onChange,
  pianoAvailable = true,
}: ModeToggleProps): React.JSX.Element {
  return (
    <div
      className="hc-modes"
      data-testid="hc-mode-toggle"
      data-mode={mode}
      role="group"
      aria-label="Where you are"
    >
      {OPTIONS.map((option) => {
        const selected = option.mode === mode;
        const disabled = option.mode === 'piano' && !pianoAvailable;
        return (
          <button
            key={option.mode}
            type="button"
            className={`hc-modes__option${selected ? ' is-selected' : ''}`}
            data-testid="hc-mode-option"
            data-mode={option.mode}
            aria-pressed={selected}
            disabled={disabled}
            title={disabled ? 'This browser cannot listen' : option.hint}
            onClick={() => onChange(option.mode)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The way out of every screen that has to say no.
 *
 * A denied microphone and a browser without `AudioWorklet` used to end in manual entry,
 * which was an escape hatch. On the Go is not a hatch — it is the whole product with a
 * different input device — so on those screens it is the *primary* action and the
 * instructions for fixing the microphone become the secondary one. Nobody who has just
 * been refused a permission wants a four-step Safari procedure before they can do anything.
 *
 * Renders nothing when the host has not wired the mode, which is what keeps every existing
 * fixture rendering exactly the screen it always did.
 */
export function ContinueOnTheGo({
  view,
  label = 'Continue on the go',
}: {
  view: SessionView;
  label?: string;
}): React.JSX.Element | null {
  const { setMode, startOnTheGo } = view;
  if (!startOnTheGo) return null;
  return (
    <button
      type="button"
      className="hc-button hc-button--primary"
      data-testid="hc-continue-on-the-go"
      onClick={() => {
        setMode?.('on-the-go');
        startOnTheGo();
      }}
    >
      {label}
    </button>
  );
}
